import { useCallback, useEffect, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { ACCOUNT_ACCENT } from './accountStyles';
import type { Territory } from './api/types';

const DEFAULT_CENTER = { lat: 43.65, lng: -79.38 };
const DEFAULT_RADIUS_M = 25_000;
let mapsOptionsInitialized = false;

interface TerritoryMapProps {
  initial: Territory | null;
  onGenerateFsas: (input: {
    centerLat: number;
    centerLng: number;
    radiusM: number;
  }) => Promise<string[]>;
  isGeneratingFsas: boolean;
  onSave: (t: {
    centerLat: number;
    centerLng: number;
    radiusM: number;
    postalFsaPrefixes?: string[];
  }) => Promise<void>;
  isSaving: boolean;
}

export const TerritoryMap = ({
  initial,
  onGenerateFsas,
  isGeneratingFsas,
  onSave,
  isSaving
}: TerritoryMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const circleRef = useRef<google.maps.Circle | null>(null);
  const [radiusM, setRadiusM] = useState(initial?.radiusM ?? DEFAULT_RADIUS_M);
  const [center, setCenter] = useState<{ lat: number; lng: number }>(() =>
    initial
      ? { lat: initial.centerLat, lng: initial.centerLng }
      : DEFAULT_CENTER
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [fsaText, setFsaText] = useState(
    (initial?.postalFsaPrefixes ?? []).join('\n')
  );
  const [isGeneratingFsa, setIsGeneratingFsa] = useState(false);
  const [generationMessage, setGenerationMessage] = useState<string | null>(
    null
  );

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined;

  useEffect(() => {
    if (initial) {
      setCenter({ lat: initial.centerLat, lng: initial.centerLng });
      setRadiusM(initial.radiusM);
      setFsaText((initial.postalFsaPrefixes ?? []).join('\n'));
    }
  }, [initial]);

  useEffect(() => {
    if (!apiKey || !mapRef.current) return;

    let cancelled = false;

    if (!mapsOptionsInitialized) {
      setOptions({
        key: apiKey,
        v: 'weekly',
        libraries: ['places', 'geometry', 'marker']
      });
      mapsOptionsInitialized = true;
    }

    void Promise.all([importLibrary('maps'), importLibrary('marker')])
      .then(() => {
        if (cancelled || !mapRef.current) return;
        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 9,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          ...(mapId ? { mapId } : {})
        });
        mapInstance.current = map;

        const marker = mapId
          ? new google.maps.marker.AdvancedMarkerElement({
              map,
              position: center,
              title: 'Territory center',
              gmpDraggable: true
            })
          : null;
        markerRef.current = marker;

        const circle = new google.maps.Circle({
          map,
          center,
          radius: radiusM,
          fillColor: '#2b6cff',
          fillOpacity: 0.12,
          strokeColor: '#2b6cff',
          strokeOpacity: 0.7,
          strokeWeight: 2,
          editable: false
        });
        circleRef.current = circle;

        marker?.addListener('dragend', () => {
          const p = marker.position;
          if (!p) return;
          const next = {
            lat: typeof p.lat === 'function' ? p.lat() : p.lat,
            lng: typeof p.lng === 'function' ? p.lng() : p.lng
          };
          setCenter(next);
          circle.setCenter(next);
        });

        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          const next = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          setCenter(next);
          if (marker) marker.position = next;
          circle.setCenter(next);
        });

        setMapReady(true);
      })
      .catch(() => {
        setLoadError('Could not load Google Maps.');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init map once per api key
  }, [apiKey]);

  useEffect(() => {
    if (!mapReady || !circleRef.current) return;
    circleRef.current.setRadius(radiusM);
    circleRef.current.setCenter(center);
    if (markerRef.current) {
      markerRef.current.position = center;
    }
  }, [radiusM, center, mapReady]);

  const generateFsa = useCallback(async () => {
    setGenerationMessage(null);
    setIsGeneratingFsa(true);
    try {
      const fsas = await onGenerateFsas({
        centerLat: center.lat,
        centerLng: center.lng,
        radiusM
      });
      if (fsas.length > 0) {
        setFsaText(fsas.join('\n'));
        setGenerationMessage(
          `Generated ${fsas.length} FSA prefix(es) from the boundary dataset.`
        );
      } else {
        setGenerationMessage(
          'No FSAs found in this radius. Try a larger radius or move the center.'
        );
      }
    } catch (e) {
      setGenerationMessage(
        e instanceof Error ? e.message : 'Could not generate FSAs.'
      );
    } finally {
      setIsGeneratingFsa(false);
    }
  }, [center.lat, center.lng, onGenerateFsas, radiusM]);

  const handleSave = useCallback(async () => {
    const postalFsaPrefixes = fsaText
      .split('\n')
      .map(x => x.trim().toUpperCase())
      .filter(Boolean);
    const centerLat = center.lat;
    const centerLng = center.lng;
    const radiusForSave = radiusM;
    await onSave({
      centerLat,
      centerLng,
      radiusM: radiusForSave,
      postalFsaPrefixes
    });
  }, [center.lat, center.lng, fsaText, onSave, radiusM]);

  if (!apiKey) {
    return (
      <div
        className={`rounded-xl p-6 ${ACCOUNT_ACCENT.cardSurface} text-sm text-slate-600`}
      >
        <p className="font-medium text-slate-800">Maps API key missing</p>
        <p className="mt-2">
          Set{' '}
          <code className="rounded bg-slate-100 px-1">
            VITE_GOOGLE_MAPS_API_KEY
          </code>{' '}
          in <code className="rounded bg-slate-100 px-1">.env.local</code> to
          set your territory center/radius and generate an FSA list.
        </p>
        <label className="mt-4 block text-xs font-medium text-slate-600">
          FSA list (one per line)
          <textarea
            value={fsaText}
            onChange={e => setFsaText(e.target.value)}
            placeholder={'M5V\nM4S\nGenerate postal code coverage from map'}
            rows={6}
            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="mt-4 rounded-lg bg-[#2b6cff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save territory'}
        </button>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
        {loadError}
      </div>
    );
  }

  return (
    <div className={`rounded-xl overflow-hidden ${ACCOUNT_ACCENT.cardSurface}`}>
      <div ref={mapRef} className="h-[min(420px,55vh)] w-full bg-slate-100" />
      <div className="flex flex-col gap-4 border-t border-slate-100 p-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void generateFsa()}
              disabled={isGeneratingFsa || isGeneratingFsas}
              className="rounded-md border border-[#2b6cff]/30 bg-[#eef4ff] px-3 py-1.5 text-xs font-semibold text-[#2b6cff] hover:bg-[#e5efff]"
            >
              {isGeneratingFsa || isGeneratingFsas
                ? 'Generating…'
                : 'Generate FSA list'}
            </button>
          </div>
          <label className="mb-2 block text-xs font-medium text-slate-600">
            Radius: {(radiusM / 1000).toFixed(1)} km
            <input
              type="range"
              min={1000}
              max={100_000}
              step={500}
              value={radiusM}
              onChange={e => setRadiusM(parseInt(e.target.value, 10))}
              className="mt-2 block w-full max-w-xs accent-[#2b6cff]"
            />
          </label>
          {generationMessage ? (
            <p className="mb-2 text-xs font-medium text-slate-600">
              {generationMessage}
            </p>
          ) : null}
          <label className="mt-3 block text-xs font-medium text-slate-600">
            FSA list (one per line)
            <textarea
              value={fsaText}
              onChange={e => setFsaText(e.target.value)}
              placeholder={'M5V\nM4S\nGenerate postal code coverage from map'}
              rows={6}
              className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
            />
            <span className="mt-1 block text-[11px] text-slate-500">
              Manual entry works too: FSA is the first 3 characters of a
              Canadian postal code (example: M5V).
            </span>
          </label>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving || !mapReady}
              className="rounded-lg bg-[#2b6cff] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save territory'}
            </button>
          </div>
        </div>
      </div>
      <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
        Radius mode only: click map to set center, adjust radius, generate FSAs,
        review/edit, then save.
        {mapId
          ? ' Drag marker is enabled.'
          : ' Add VITE_GOOGLE_MAPS_MAP_ID to enable draggable advanced marker.'}
      </p>
    </div>
  );
};
